/**
 * Continuous state beds: integrity drone/pulse, tilt morph, will bleed, proclaim bed.
 * Node budget: ≤8 continuous sources (5 loops + scheduled pulse one-shots).
 *
 * Ownership: only engine.tick — Game never touches these nodes.
 */

import {
  integrityBandWithHysteresis,
  integrityPulseBpmFromFrac,
  integrityPulseGainFromFrac,
  leanBand,
  type IntegrityBand,
  type LeanBand,
} from "./bands";
import type { AudioBusGraph } from "./buses";
import type { ClipId } from "./catalog";
import {
  integrityFrac,
  isProclaiming,
  willBleedIntensity,
  type AudioFrameInput,
} from "./frame";
import type { AudioSettings } from "./settings";

type LayerId = "mind" | "heart" | "will";
const LAYERS: LayerId[] = ["mind", "heart", "will"];

/** Filter cutoff (Hz) — Mind brighter, Will darker, mono-safe. */
const LAYER_LOWPASS: Record<LayerId, number> = {
  mind: 4200,
  heart: 2200,
  will: 900,
};

const CLIP = {
  drone: "state.drone" as ClipId,
  pulse: "state.pulse-thump" as ClipId,
  truth: "state.tilt-truth" as ClipId,
  lie: "state.tilt-lie" as ClipId,
  bleed: "state.will-bleed" as ClipId,
  proclaim: "state.proclaim-bed" as ClipId,
};

export type BufferResolver = (id: ClipId) => AudioBuffer | null;

type LoopBed = {
  source: AudioBufferSourceNode | null;
  gain: GainNode;
};

type LayerTiltNodes = {
  truthGain: GainNode;
  lieGain: GainNode;
  filter: BiquadFilterNode;
};

function droneGainForBand(band: IntegrityBand): number {
  switch (band) {
    case "rest":
      return 0.12;
    case "alert":
      return 0.18;
    case "urgent":
      return 0.24;
    case "critical":
      return 0.32;
  }
}

/** Map lean band → truth/lie mix weights (0–1). */
function leanMix(band: LeanBand): { truth: number; lie: number } {
  switch (band) {
    case "truth-strong":
      return { truth: 0.42, lie: 0 };
    case "truth":
      return { truth: 0.28, lie: 0.04 };
    case "contested":
      return { truth: 0.1, lie: 0.1 };
    case "lie":
      return { truth: 0.04, lie: 0.28 };
    case "lie-strong":
      return { truth: 0, lie: 0.42 };
  }
}

/**
 * Smooth gain toward target. Avoid cancelScheduledValues every frame —
 * that zippers and reads as scratch between heartbeats when the drone is up.
 */
function rampGain(
  param: AudioParam,
  target: number,
  now: number,
  timeConstant: number,
  lastTarget: { v: number },
): void {
  const next = Math.max(0, target);
  // Only re-target when the desired level actually moves
  if (Math.abs(next - lastTarget.v) < 0.008) return;
  lastTarget.v = next;
  const t = Math.max(0.05, timeConstant);
  try {
    // setTarget alone is continuous; cancel only when we intentionally retarget
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.setTargetAtTime(next, now, t / 3);
  } catch {
    param.value = next;
  }
}

export class StateSonifier {
  private readonly context: AudioContext;
  private readonly graph: AudioBusGraph;
  private resolveBuffer: BufferResolver;

  private integrityBand: IntegrityBand = "rest";
  private started = false;
  private disposed = false;

  private drone: LoopBed;
  private bleed: LoopBed;
  private proclaim: LoopBed;
  /** Quiet mutual-contest grind (shares lie texture source — no extra buffer source). */
  private contestGain: GainNode;
  private truthSource: AudioBufferSourceNode | null = null;
  private lieSource: AudioBufferSourceNode | null = null;

  private layers: Record<LayerId, LayerTiltNodes>;

  /** Last ramp targets so we do not cancel AudioParams every frame. */
  private lastDroneG = { v: -1 };
  private lastBleedG = { v: -1 };
  private lastProclaimG = { v: -1 };
  private lastContestG = { v: -1 };
  private lastTruthG: Record<LayerId, { v: number }> = {
    mind: { v: -1 },
    heart: { v: -1 },
    will: { v: -1 },
  };
  private lastLieG: Record<LayerId, { v: number }> = {
    mind: { v: -1 },
    heart: { v: -1 },
    will: { v: -1 },
  };

  /** When to fire next integrity pulse (AudioContext time). */
  private nextPulseTime = 0;
  private pulseSources: AudioBufferSourceNode[] = [];

  constructor(
    context: AudioContext,
    graph: AudioBusGraph,
    resolveBuffer: BufferResolver,
  ) {
    this.context = context;
    this.graph = graph;
    this.resolveBuffer = resolveBuffer;

    const mkGain = (v = 0) => {
      const g = context.createGain();
      g.gain.value = v;
      return g;
    };

    this.drone = { source: null, gain: mkGain(0) };
    this.bleed = { source: null, gain: mkGain(0) };
    this.proclaim = { source: null, gain: mkGain(0) };
    this.contestGain = mkGain(0);

    this.drone.gain.connect(graph.getBus("state"));
    this.bleed.gain.connect(graph.getBus("state"));
    this.proclaim.gain.connect(graph.getBus("state"));
    this.contestGain.connect(graph.getBus("state"));

    this.layers = {} as Record<LayerId, LayerTiltNodes>;
    for (const id of LAYERS) {
      const truthGain = mkGain(0);
      const lieGain = mkGain(0);
      const filter = context.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = LAYER_LOWPASS[id];
      filter.Q.value = 0.7;
      truthGain.connect(filter);
      lieGain.connect(filter);
      filter.connect(graph.getBus("state"));
      this.layers[id] = { truthGain, lieGain, filter };
    }
  }

  setBufferResolver(fn: BufferResolver): void {
    this.resolveBuffer = fn;
  }

  /**
   * Start loop carriers when buffers are available.
   * Safe to call repeatedly.
   */
  tryStart(): void {
    if (this.disposed || this.started) return;
    const droneB = this.resolveBuffer(CLIP.drone);
    const truthB = this.resolveBuffer(CLIP.truth);
    const lieB = this.resolveBuffer(CLIP.lie);
    const bleedB = this.resolveBuffer(CLIP.bleed);
    const procB = this.resolveBuffer(CLIP.proclaim);
    if (!droneB || !truthB || !lieB || !bleedB || !procB) return;

    this.startLoop(this.drone, droneB);
    this.startLoop(this.bleed, bleedB);
    this.startLoop(this.proclaim, procB);

    // 2 shared textures → per-layer mix gains (not 3× exclusive sources)
    const truthSrc = this.context.createBufferSource();
    truthSrc.buffer = truthB;
    truthSrc.loop = true;
    for (const id of LAYERS) {
      truthSrc.connect(this.layers[id].truthGain);
    }
    truthSrc.start();
    this.truthSource = truthSrc;

    const lieSrc = this.context.createBufferSource();
    lieSrc.buffer = lieB;
    lieSrc.loop = true;
    for (const id of LAYERS) {
      lieSrc.connect(this.layers[id].lieGain);
    }
    // Shared lie texture → quiet contest grind (no 6th loop source)
    lieSrc.connect(this.contestGain);
    lieSrc.start();
    this.lieSource = lieSrc;

    this.started = true;
  }

  private startLoop(bed: LoopBed, buffer: AudioBuffer): void {
    if (bed.source) return;
    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(bed.gain);
    try {
      src.start();
    } catch {
      /* */
    }
    bed.source = src;
  }

  tick(frame: AudioFrameInput, _dt: number, settings: AudioSettings): void {
    if (this.disposed) return;
    this.tryStart();
    if (!this.started) return;

    const now = this.context.currentTime;
    const hidden =
      typeof document !== "undefined" ? document.hidden : false;
    const silent =
      settings.muted ||
      settings.minimal ||
      frame.status !== "playing" ||
      hidden;

    if (silent || frame.paused) {
      this.fadeAll(now, frame.paused && !silent ? 0.4 : 0.25);
      this.clearPulses();
      return;
    }

    const frac = integrityFrac(frame);
    this.integrityBand = integrityBandWithHysteresis(
      this.integrityBand,
      frac,
      0.03,
    );

    // Integrity drone — attack ~0.8s / release ~1.2s via setTarget
    rampGain(
      this.drone.gain.gain,
      droneGainForBand(this.integrityBand),
      now,
      this.integrityBand === "critical" ? 0.6 : 0.9,
      this.lastDroneG,
    );

    // Continuous tempo/gain from frac — no band steps, no schedule hard-reset
    this.schedulePulses(
      integrityPulseBpmFromFrac(frac),
      integrityPulseGainFromFrac(frac),
      now,
    );

    // Tilt morph: same lean predicates as UI; ~0.5–0.7s morphs
    for (const id of LAYERS) {
      const mix = leanMix(leanBand(frame.tilts[id]));
      const L = this.layers[id];
      rampGain(
        L.truthGain.gain,
        mix.truth,
        now,
        0.55,
        this.lastTruthG[id],
      );
      rampGain(
        L.lieGain.gain,
        mix.lie,
        now,
        0.65,
        this.lastLieG[id],
      );
    }

    // Will bleed grit when Will is open
    const bleed =
      !frame.willHeld && frame.willEnemyCount > 0
        ? willBleedIntensity(frame) * 0.35
        : 0;
    rampGain(
      this.bleed.gain.gain,
      bleed,
      now,
      bleed > 0 ? 0.4 : 1.0,
      this.lastBleedG,
    );

    // Contest grind: only when intensity above threshold
    const contestRaw = frame.contestIntensity ?? 0;
    const contest = contestRaw > 0.05 ? contestRaw * 0.16 : 0;
    rampGain(
      this.contestGain.gain,
      contest,
      now,
      contest > 0 ? 0.6 : 1.2,
      this.lastContestG,
    );

    // Proclaim speech bed — P3 only (MHW sets lock 0)
    const proc = isProclaiming(frame) ? 0.28 : 0;
    rampGain(
      this.proclaim.gain.gain,
      proc,
      now,
      proc > 0 ? 0.15 : 0.35,
      this.lastProclaimG,
    );
  }

  /**
   * Schedule heart thumps. Tempo morphs beat-to-beat from the current BPM
   * (no cancel/reschedule on small changes — that felt like a sudden jump).
   */
  private schedulePulses(bpm: number, gain: number, now: number): void {
    const safeBpm = Math.max(40, Math.min(140, bpm));
    if (this.nextPulseTime <= 0) {
      // First beat a little out so unlock/start is clean
      this.nextPulseTime = now + 0.12;
    }
    // Tab freeze / long hitch — resync once, keep a steady stream
    if (this.nextPulseTime < now - 0.05) {
      this.nextPulseTime = now + 0.02;
    }
    const interval = 60 / safeBpm;
    // Short lookahead: one beat ahead so we never pile / steal voices
    let guard = 0;
    while (this.nextPulseTime < now + 0.2 && guard++ < 2) {
      const t = this.nextPulseTime;
      this.playPulseOneShot(gain, t);
      this.nextPulseTime += interval;
    }
  }

  private playPulseOneShot(gain: number, when: number): void {
    const buffer = this.resolveBuffer(CLIP.pulse);
    if (!buffer) return;
    // Never hard-stop a previous pulse (that was the scratch between beats)
    if (this.pulseSources.length >= 3) return;

    try {
      const src = this.context.createBufferSource();
      src.buffer = buffer;
      const g = this.context.createGain();
      // Envelope on the gain node: soft attack, full release with buffer
      const peak = Math.max(0.001, gain);
      const start = Math.max(when, this.context.currentTime);
      const dur = buffer.duration;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(peak, start + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, start + Math.max(0.05, dur * 0.95));

      src.connect(g);
      g.connect(this.graph.getBus("state"));
      src.start(start);
      src.stop(start + dur + 0.02);
      this.pulseSources.push(src);
      src.onended = () => {
        const i = this.pulseSources.indexOf(src);
        if (i >= 0) this.pulseSources.splice(i, 1);
        try {
          src.disconnect();
          g.disconnect();
        } catch {
          /* */
        }
      };
    } catch {
      /* */
    }
  }

  private clearPulses(): void {
    this.nextPulseTime = 0;
    const now = this.context.currentTime;
    for (const s of this.pulseSources) {
      try {
        // Fade-stop if possible: just stop slightly in the future
        s.stop(now + 0.02);
      } catch {
        try {
          s.stop();
        } catch {
          /* */
        }
      }
      try {
        s.disconnect();
      } catch {
        /* */
      }
    }
    this.pulseSources = [];
  }

  private fadeAll(now: number, tc: number): void {
    rampGain(this.drone.gain.gain, 0, now, tc, this.lastDroneG);
    rampGain(this.bleed.gain.gain, 0, now, tc, this.lastBleedG);
    rampGain(this.proclaim.gain.gain, 0, now, tc, this.lastProclaimG);
    rampGain(this.contestGain.gain, 0, now, tc, this.lastContestG);
    for (const id of LAYERS) {
      rampGain(this.layers[id].truthGain.gain, 0, now, tc, this.lastTruthG[id]);
      rampGain(this.layers[id].lieGain.gain, 0, now, tc, this.lastLieG[id]);
    }
  }

  resetMission(): void {
    this.clearPulses();
    this.integrityBand = "rest";
    this.lastDroneG.v = -1;
    this.lastBleedG.v = -1;
    this.lastProclaimG.v = -1;
    this.lastContestG.v = -1;
    for (const id of LAYERS) {
      this.lastTruthG[id].v = -1;
      this.lastLieG[id].v = -1;
    }
    if (this.started) {
      this.fadeAll(this.context.currentTime, 0.15);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearPulses();

    for (const bed of [this.drone, this.bleed, this.proclaim]) {
      try {
        bed.source?.stop();
        bed.source?.disconnect();
      } catch {
        /* */
      }
      bed.source = null;
      try {
        bed.gain.disconnect();
      } catch {
        /* */
      }
    }
    for (const src of [this.truthSource, this.lieSource]) {
      try {
        src?.stop();
        src?.disconnect();
      } catch {
        /* */
      }
    }
    this.truthSource = null;
    this.lieSource = null;

    for (const id of LAYERS) {
      const L = this.layers[id];
      try {
        L.truthGain.disconnect();
        L.lieGain.disconnect();
        L.filter.disconnect();
      } catch {
        /* */
      }
    }
    try {
      this.contestGain.disconnect();
    } catch {
      /* */
    }
    this.started = false;
  }
}
